import React from "react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/lib/theme";

export default function ThemeToggle({ variant = "default", testid = "theme-toggle" }) {
    const { theme, toggle } = useTheme();
    const isDark = theme === "dark";
    const isSidebar = variant === "sidebar";

    function handleClick(e) {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        document.documentElement.style.setProperty("--reveal-x", `${x}px`);
        document.documentElement.style.setProperty("--reveal-y", `${y}px`);

        // Use View Transitions API when supported for circle-reveal flip.
        if (typeof document !== "undefined" && document.startViewTransition) {
            document.startViewTransition(() => toggle());
        } else {
            toggle();
        }
    }

    if (isSidebar) {
        return (
            <button
                onClick={handleClick}
                data-testid={testid}
                role="switch"
                aria-checked={isDark}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                className="group w-full relative h-9 flex items-center px-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[var(--surface-container-high)] rounded-full transition-colors overflow-hidden"
                style={{ border: "1px solid var(--outline-variant)" }}
            >
                <span
                    className="absolute top-1 bottom-1 w-7 rounded-full bg-[var(--primary)] transition-all duration-500 motion-reduce:transition-none"
                    style={{
                        left: isDark ? "calc(100% - 30px)" : "2px",
                    }}
                />
                <span
                    className="relative z-10 flex-1 text-center transition-colors"
                    style={{ color: isDark ? "var(--text-muted)" : "var(--on-primary)" }}
                >
                    Light
                </span>
                <span
                    className="relative z-10 flex-1 text-center transition-colors"
                    style={{ color: isDark ? "var(--on-primary)" : "var(--text-muted)" }}
                >
                    Dark
                </span>
            </button>
        );
    }

    if (variant === "nav") {
        return (
            <button
                onClick={handleClick}
                data-testid={testid}
                role="switch"
                aria-checked={isDark}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                title={isDark ? "Light mode" : "Dark mode"}
                className="self-stretch px-6 grid place-items-center bg-transparent cursor-pointer border-none outline-none hover:bg-[var(--surface-container-highest)] transition-colors duration-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
            >
                {isDark ? <Sun size={16} strokeWidth={2} /> : <Moon size={16} strokeWidth={2} />}
            </button>
        );
    }

    return (
        <button
            onClick={handleClick}
            data-testid={testid}
            role="switch"
            aria-checked={isDark}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Light mode" : "Dark mode"}
            className="relative w-11 h-11 rounded-full grid place-items-center hover:bg-[var(--surface-container-high)] transition-colors overflow-hidden"
            style={{ border: "1px solid var(--outline-variant)" }}
        >
            <span
                className="absolute inset-0 grid place-items-center transition-all duration-500 motion-reduce:transition-none"
                style={{
                    transform: isDark ? "rotate(0deg) scale(1)" : "rotate(90deg) scale(0)",
                    opacity: isDark ? 1 : 0,
                }}
            >
                <Sun size={16} strokeWidth={2} />
            </span>
            <span
                className="absolute inset-0 grid place-items-center transition-all duration-500 motion-reduce:transition-none"
                style={{
                    transform: isDark ? "rotate(-90deg) scale(0)" : "rotate(0deg) scale(1)",
                    opacity: isDark ? 0 : 1,
                }}
            >
                <Moon size={16} strokeWidth={2} />
            </span>
        </button>
    );
}
